import React, { useState, useCallback } from 'react';
import { API_URL } from './../constants';
import { useContext } from './../Context';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import ImagePreviewSkeleton from './ImagePreviewSkeleton';
import './ImageUpload.css';
import ImagePreview from './ImagePreview';
import CropModal from './CropModal';

const ImageUpload = () => {
    const { globalState, setGlobalState } = useContext();
    const { limit, activeFilter } = globalState;
    const [isLoading, setIsLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [crop, setCrop] = useState({ aspect: 1 });
    const [croppedImageUrl, setCroppedImageUrl] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [originalFile, setOriginalFile] = useState(null); // Add this state for the original file


    const onDrop = useCallback((acceptedFiles) => {
        setIsLoading(true);
        const file = acceptedFiles[0];
        setOriginalFile(file); // Store the original file
        const reader = new FileReader();
        reader.onload = () => {
            setSelectedImage(reader.result);
            setIsModalOpen(true); // Open the modal when the image is selected
        };
        reader.readAsDataURL(file);
    }, []);

    const onCropComplete = async (crop) => {
        if (selectedImage && crop.width && crop.height) {
            const croppedImage = await getCroppedImg(selectedImage, crop);
            setCroppedImageUrl(croppedImage);
        }
    };

    const getCroppedImg = (imageSrc, crop) => {
        const canvas = document.createElement('canvas');
        const image = new Image();
        image.src = imageSrc;

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                const fileUrl = URL.createObjectURL(blob);
                resolve(fileUrl);
            }, 'image/jpeg');
        });
    };

    const onFileUpload = async (file, type = 'upload') => {

        try {
            // Determine if the cropped image should be used or the original file
            const finalFile = croppedImageUrl ? await fetch(croppedImageUrl).then(res => res.blob()) : file;

            // Push data to the Data Layer for Google Analytics tracking
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'aiFileUpload',
                fileName: file.name,
                fileSize: file.size, // File size in bytes
                filters: activeFilter, // Assuming `activeFilter` is the filter applied
                appVersion: 'webApp',
                timestamp: new Date().toISOString()
            });

            setGlobalState((prevState) => ({
                ...prevState,
                uploadMode: 'blob',
                selectedFile: finalFile,
                isMatchLoading: true
            }))

            setIsLoading(false)

            const formData = new FormData();
            formData.append('image', finalFile, file.name);
            formData.append('limit', limit);
            formData.append('product_type', activeFilter);

            const response = await axios.post(`${API_URL}/check-image-against-library`, formData);

            setGlobalState((prevState) => ({
                ...prevState,
                match: response.data.closestMatch,
                topMatches: response.data.topMatches,
                timeTaken: response.data.timeTaken,
                librarySize: response.data.librarySize,
                isMatchLoading: false
            }));
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
            setIsModalOpen(false); // Close the modal after upload
        }
    };

    const handleUpload = () => {
        if (selectedImage && croppedImageUrl) {
            onFileUpload(croppedImageUrl);
        } else if (selectedImage) {
            onFileUpload(originalFile);
        }
        setCrop({ aspect: 1 }); // Reset crop area
        setIsModalOpen(false);
    };

    const skipCropping = () => {
        setIsModalOpen(false);
        setCrop({ aspect: 1 }); // Reset crop area
        handleUpload();
    };

    const { getRootProps, getInputProps } = useDropzone({ onDrop });

    return (
        <>
            <CropModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <p>Click & drag your cursor to crop your image</p>
                <div className='crop-image-ui has-border-radius'>
                    <div className='mb1'>
                        {selectedImage && (
                            <ReactCrop
                                crop={crop}
                                onChange={(newCrop) => setCrop(newCrop)}
                                onComplete={onCropComplete}
                            >
                                <img src={selectedImage} alt="selected option" />
                            </ReactCrop>
                        )}
                    </div>
                    <div className='button-group'>
                        <button className="mr1" onClick={handleUpload}>Use Cropped Image</button>
                        <button onClick={skipCropping}>Skip Cropping</button>
                    </div>
                </div>
            </CropModal>
            <div className='image-upload cursor-pointer upload-zone' {...getRootProps()}>
                <div>
                    <input {...getInputProps()} />
                    <h4>Image Match</h4>
                    <p>Drag & drop an image here / click inside this box</p>
                </div>

                {isLoading ? (
                    <ImagePreviewSkeleton />
                ) : (
                    <ImagePreview />
                )}


            </div>
        </>
    );
};

export default ImageUpload;
